/**
 * `ConnectedAppsSetupSteps` — the segmented control inside the
 * Connected apps panel that reveals the one-step setup for each
 * assistant client (ADR-0261 §5).
 *
 * Four tabs:
 *   - Claude            (claude.ai web app)
 *   - Claude Desktop
 *   - Claude Code       (CLI)
 *   - ChatGPT
 *
 * Claude Code is the only tab with an exact machine-readable command
 * (`claude mcp add --transport http sill https://api.sill.so/mcp`
 * followed by `/mcp` → Sill → sign in). The other three use generic
 * wording because the vendor menu labels change too often to be
 * worth pinning in Sill's UI; the shape of the action is identical
 * ("add a custom connector with the server URL, then approve the
 * permissions on the Sill page that opens").
 *
 * Self-contained: the component owns the active-tab state. The
 * server URL is passed in because the parent panel also renders it
 * at the top of the card next to the Copy button.
 */

import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type ClientTab = 'claude' | 'claude_desktop' | 'claude_code' | 'chatgpt' | 'other';

const TABS: readonly { readonly id: ClientTab; readonly label: string }[] = [
  { id: 'claude', label: 'Claude' },
  { id: 'claude_desktop', label: 'Claude Desktop' },
  { id: 'claude_code', label: 'Claude Code' },
  { id: 'chatgpt', label: 'ChatGPT' },
  // Shown only when the API reports self-registration (dynamic client
  // registration) is on. Named assistants are added here as each one is
  // verified against a real connection, never before.
  { id: 'other', label: 'Other assistants' },
];

const OTHER_INSTRUCTION_1 =
  'Any assistant that supports remote MCP servers with OAuth sign-in can connect. In its connector or integrations settings, add a custom MCP server with the URL above, choose OAuth, and approve the permissions on the Sill page that opens.';
const OTHER_INSTRUCTION_2 =
  'Assistants that register themselves rather than publishing a verified identity are read-only: they can read sites, analytics, security activity, held orders, readiness and the audit log, but cannot block senders, ask for order reviews or run checks.';

const GENERIC_INSTRUCTION =
  "In your assistant's connector settings, add a custom connector with the URL above, then approve the permissions on the Sill page that opens.";

export interface ConnectedAppsSetupStepsProps {
  readonly server_url: string;
  /** From `GET /v1/oauth/grants` → `dcr_enabled`. Defaults to hidden. */
  readonly other_assistants_enabled?: boolean;
}

export function ConnectedAppsSetupSteps({
  server_url,
  other_assistants_enabled = false,
}: ConnectedAppsSetupStepsProps): JSX.Element {
  const C = useC();
  const [tab, setTab] = useState<ClientTab>('claude');
  const visible_tabs = TABS.filter((t) => t.id !== 'other' || other_assistants_enabled);

  return (
    <div>
      <div
        role="tablist"
        aria-label="Setup steps per assistant"
        className="flex flex-wrap gap-1 mb-4"
      >
        {visible_tabs.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => setTab(t.id)}
              className="px-3 py-1.5"
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                color: active ? C.text : C.muted,
                background: active ? C.surface2 : 'transparent',
                border: `1px solid ${active ? C.border : 'transparent'}`,
                borderRadius: 2,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel">
        {tab === 'claude_code' ? (
          <ClaudeCodeSteps server_url={server_url} />
        ) : tab === 'chatgpt' ? (
          <ChatGptSteps server_url={server_url} />
        ) : tab === 'claude' || tab === 'claude_desktop' ? (
          <ClaudeSteps server_url={server_url} />
        ) : tab === 'other' ? (
          <OtherAssistantsSteps />
        ) : (
          <GenericSteps />
        )}
      </div>
    </div>
  );
}

function OtherAssistantsSteps(): JSX.Element {
  const C = useC();
  return (
    <div
      data-testid="connected-apps-other-assistants"
      className="flex flex-col gap-2"
      style={{
        fontFamily: sansFont,
        fontSize: 13,
        color: C.muted,
        lineHeight: 1.6,
        maxWidth: 560,
      }}
    >
      <div>{OTHER_INSTRUCTION_1}</div>
      <div>{OTHER_INSTRUCTION_2}</div>
    </div>
  );
}

function GenericSteps(): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        fontFamily: sansFont,
        fontSize: 13,
        color: C.muted,
        lineHeight: 1.6,
        maxWidth: 560,
      }}
    >
      {GENERIC_INSTRUCTION}
    </div>
  );
}

function Steps({ children }: { readonly children: ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div className="flex flex-col gap-3" style={{ maxWidth: 640 }}>
      <ol
        className="list-decimal pl-5"
        style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.7 }}
      >
        {children}
      </ol>
    </div>
  );
}

/** ChatGPT's own labels as of 2026-10 (chatgpt.com/plugins → Add custom MCP server). */
function ChatGptSteps({ server_url }: { readonly server_url: string }): JSX.Element {
  return (
    <Steps>
      <li>
        In ChatGPT on the web, open <strong>Plugins</strong>, click the plus button and choose{' '}
        <strong>Add custom MCP server</strong>.
      </li>
      <li style={{ marginTop: 10 }}>
        Name it Sill. Under <strong>Connection</strong>, paste the server URL:
        <div style={{ marginTop: 6 }}>
          <CopyRow value={server_url} ariaLabel="Copy server URL for ChatGPT" />
        </div>
      </li>
      <li style={{ marginTop: 10 }}>
        Choose <strong>OAuth</strong> as the authentication, accept the risk notice and click{' '}
        <strong>Create as a plugin</strong>.
      </li>
      <li style={{ marginTop: 10 }}>
        Install the plugin, sign in on the Sill page that opens and approve the permissions.
      </li>
      <li style={{ marginTop: 10 }}>
        In a chat, type <strong>@</strong> and pick Sill. ChatGPT asks you to confirm each action that
        changes something.
      </li>
    </Steps>
  );
}

/** Claude.ai, Claude Desktop and Claude mobile share one connector list. */
function ClaudeSteps({ server_url }: { readonly server_url: string }): JSX.Element {
  return (
    <Steps>
      <li>
        Open <strong>Settings</strong> → <strong>Connectors</strong> and choose{' '}
        <strong>Add custom connector</strong>.
      </li>
      <li style={{ marginTop: 10 }}>
        Name it Sill and paste the server URL:
        <div style={{ marginTop: 6 }}>
          <CopyRow value={server_url} ariaLabel="Copy server URL for Claude" />
        </div>
      </li>
      <li style={{ marginTop: 10 }}>
        Leave the client id fields empty so Claude uses its own identity, then click{' '}
        <strong>Add</strong>.
      </li>
      <li style={{ marginTop: 10 }}>
        Click <strong>Connect</strong>, sign in on the Sill page that opens and approve the permissions.
      </li>
    </Steps>
  );
}

function ClaudeCodeSteps({ server_url }: { readonly server_url: string }): JSX.Element {
  const C = useC();
  const command = `claude mcp add --transport http sill ${server_url}`;
  return (
    <div className="flex flex-col gap-3" style={{ maxWidth: 640 }}>
      <ol
        className="list-decimal pl-5"
        style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.7 }}
      >
        <li>
          Run this in your terminal:
          <div style={{ marginTop: 6 }}>
            <CopyRow value={command} ariaLabel="Copy Claude Code setup command" />
          </div>
        </li>
        <li style={{ marginTop: 10 }}>
          In Claude Code, run{' '}
          <code
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              background: C.surface2,
              border: `1px solid ${C.border}`,
              padding: '1px 6px',
              borderRadius: 2,
            }}
          >
            /mcp
          </code>
          .
        </li>
        <li style={{ marginTop: 10 }}>Select Sill and sign in on the page that opens.</li>
      </ol>
    </div>
  );
}

function CopyRow({
  value,
  ariaLabel,
}: {
  readonly value: string;
  readonly ariaLabel: string;
}): JSX.Element {
  const C = useC();
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(value);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div
      className="px-2.5 py-1.5 flex items-center gap-2"
      style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
    >
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color: C.text,
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {value}
      </span>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={copy}
        className="flex-shrink-0 p-1"
        style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2 }}
      >
        {copied ? <Check size={11} color={C.limeFg} /> : <Copy size={11} color={C.muted} />}
      </button>
    </div>
  );
}
